<template>
  <div style="display: flex; align-items: center; width: 100%">
    <span
      ref="trigger"
      class="pill-badge"
      :style="{
        ...pillColorStyleObject(currentStatusConfig),
        cursor: isLocked ? 'default' : 'pointer',
      }"
      @click.stop="!isLocked && toggleDropdown()"
    >
      <span class="status-pill-label">{{ currentStatusConfig.label }}</span>
      <i
        v-if="!isLocked"
        :class="showDropdown ? 'fas fa-caret-up' : 'fas fa-caret-down'"
        class="status-pill-caret"
        :style="{ color: currentStatusConfig.color }"
      ></i>
    </span>

    <div v-if="showDropdown" ref="dropdown" class="status-dropdown" :style="dropdownStyle" @click.stop>
      <div class="status-dropdown-header">VERSCHIEBEN NACH</div>
      <!-- Hinweis wenn keine Statuswechsel möglich sind -->
      <div v-if="availableStatuses.length === 0" class="status-dropdown-empty">Keine Aktionen verfügbar</div>
      <div
        v-for="option in availableStatuses"
        :key="option.trigger"
        class="status-dropdown-item"
        @click.stop="selectStatus(option)"
      >
        <span class="pill-badge" :style="pillColorStyleObject(option)">
          {{ option.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { LOAD_TRIGGERS } from '@/core/aufgaben/stores/aufgaben.module';
import { aufgabenStatusColors, pillColorStyleObject } from '@/core/common/helpers/utils';

// Mapping von Trigger-Namen aus der API
const triggerConfig = {
  TriggerOffen: { label: 'Offen', ...aufgabenStatusColors.Offen },
  TriggerInArbeit: { label: 'In Arbeit', ...aufgabenStatusColors.InArbeit },
  TriggerPending: { label: 'Pending', ...aufgabenStatusColors.Pending },
  TriggerErledigt: { label: 'Erledigt', ...aufgabenStatusColors.Erledigt },
};

// Mapping für das Status-Badge in der Grid-Zelle
const statusConfigMap = {
  Offen: { label: 'Offen', ...aufgabenStatusColors.Offen },
  InArbeit: { label: 'In Arbeit', ...aufgabenStatusColors.InArbeit },
  Pending: { label: 'Pending', ...aufgabenStatusColors.Pending },
  Erledigt: { label: 'Erledigt', ...aufgabenStatusColors.Erledigt },
};

let activeInstance = null;

export default {
  data() {
    return {
      params: null,
      showDropdown: false,
      dropdownStyle: {},
      availableStatuses: [],
      loadingTriggers: false,
    };
  },
  computed: {
    // Aktueller Status-Wert der Aufgabe
    currentStatus() {
      return this.params?.data?.status;
    },
    currentStatusConfig() {
      return statusConfigMap[this.currentStatus] || statusConfigMap['Offen'];
    },
    // Erledigt seit mehr als 1 Stunde → kein Statuswechsel mehr möglich
    isLocked() {
      if (this.currentStatus !== 'Erledigt') return false;
      const erledigtDatum = this.params?.data?.statusErledigtDatum;
      if (!erledigtDatum) return false;
      const diffMs = Date.now() - new Date(erledigtDatum).getTime();
      return diffMs > 60 * 60 * 1000;
    },
  },
  methods: {
    // AG Grid Lifecycle: Wird aufgerufen wenn die Komponente initialisiert wird.
    agInit(params) {
      this.params = params;
    },

    pillColorStyleObject,

    // AG Grid Lifecycle: Wird aufgerufen wenn sich die Zeilendaten ändern.
    refresh(params) {
      this.params = params;
      this.showDropdown = false;
      return true;
    },

    async toggleDropdown() {
      // Dropdown schließen wenn es bereits offen ist
      if (this.showDropdown) {
        this.closeDropdown();
        return;
      }

      // Sicherstellen, dass nur ein Dropdown gleichzeitig offen ist
      if (activeInstance && activeInstance !== this) {
        activeInstance.closeDropdown();
      }

      // Erlaubte Status-Übergänge von der Backend State-Machine laden
      this.loadingTriggers = true;
      try {
        const triggers = await this.$store.dispatch(LOAD_TRIGGERS, { aufgabeId: this.params.data.id });
        const kategorien = this.$store.getters.getKategorienWithErgebnisFesthalten || [];
        const isErgebnisFesthalten = kategorien.includes(this.params.data?.kategorie);

        this.availableStatuses = (triggers || [])
          .filter(t => t.allowed || (t.trigger === 'TriggerErledigt' && isErgebnisFesthalten))
          .map(t => ({ trigger: t.trigger, ...triggerConfig[t.trigger] }))
          .filter(t => t.label); // Unbekannte Trigger ausfiltern
      } catch (e) {
        console.error('Fehler beim Laden der Triggers:', e);
        this.availableStatuses = [];
      }
      this.loadingTriggers = false;

      // Dropdown-Position berechnen: nach unten oder oben öffnen
      const rect = this.$refs.trigger.getBoundingClientRect();
      const dropdownHeight = this.availableStatuses.length * 36 + 40; // Geschätzte Höhe (Items + Header)
      const openUpward = rect.bottom + dropdownHeight + 8 > window.innerHeight;

      this.dropdownStyle = {
        position: 'fixed',
        left: rect.left + 'px',
        zIndex: 9999,
        // Nach oben öffnen wenn unten nicht genug Platz ist
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 + 'px' }
          : { top: rect.bottom + 4 + 'px' }),
      };

      this.showDropdown = true;
      activeInstance = this;

      this.$nextTick(() => {
        if (this.$refs.dropdown) {
          document.body.appendChild(this.$refs.dropdown);
        }
      });
    },

    closeDropdown() {
      if (this.showDropdown && this.$refs.dropdown && this.$refs.dropdown.parentNode === document.body) {
        document.body.removeChild(this.$refs.dropdown);
      }
      this.showDropdown = false;
      if (activeInstance === this) {
        activeInstance = null;
      }
    },

    selectStatus(option) {
      this.closeDropdown();
      if (this.params.onStatusChange) {
        this.params.onStatusChange(this.params.data, option.trigger);
      }
    },

    handleOutsideClick(e) {
      if (
        this.showDropdown &&
        !this.$refs.dropdown?.contains(e.target) &&
        !this.$refs.trigger?.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    this.closeDropdown();
  },
};
</script>

<style scoped>
.status-pill-label {
  width: 58px;
  text-align: center;
}
.status-pill-caret {
  margin-left: 6px;
  font-size: 12px;
}
.status-dropdown {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  overflow: hidden;
}
.status-dropdown-header {
  padding: 8px 10px;
  font-size: 10px;
  font-weight: 500;
  color: #6a7282;
  border-bottom: 1px solid #f0f0f0;
}
.status-dropdown-empty {
  padding: 8px 10px;
  font-size: 10px;
  color: var(--text-muted);
}
.status-dropdown-item {
  padding: 8px 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.15s;
}
.status-dropdown-item:hover {
  background-color: #f5f5f5;
}
</style>
